import { useClientLogger } from '@coolblue-development/next-config/client';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { PostMessageRequestTypes } from '../../../../domain/chatbot/dto/postMessageRequestTypes.js';
import { UiMessageTypes } from '../../../../domain/chatbot/dto/uiMessageTypes.js';
import { PostMessageResponseTypes } from '../../../../types.js';
function streamReducer(state, action) {
    switch (action.type) {
        case 'ADD_MESSAGE':
            return {
                ...state,
                uiMessages: [...state.uiMessages, action.payload],
            };
        case 'REMOVE_LAST_MESSAGE':
            return {
                ...state,
                uiMessages: state.uiMessages.slice(0, -1),
            };
        case 'REPLACE_LAST_MESSAGE':
            return {
                ...state,
                uiMessages: state.uiMessages.slice(0, -1).concat(action.payload),
            };
        case 'SET_INPUT_DISABLED':
            return {
                ...state,
                isStreaming: true,
            };
        case 'SET_INPUT_ENABLED':
            return {
                ...state,
                isStreaming: false,
            };
        case 'SET_SHOW_ERROR_TRUE':
            return {
                ...state,
                showError: true,
            };
        case 'SET_STATE':
            return {
                ...action.payload,
            };
        case 'START_USER_MESSAGE_PROCESSING':
            return {
                ...state,
                isStreaming: true,
                uiMessages: [
                    ...state.uiMessages,
                    action.payload.userMessage,
                    action.payload.loadingMessage,
                ],
            };
        default:
            return state;
    }
}
const createUserUiTextMessage = (text) => ({
    isUser: true,
    text,
    type: UiMessageTypes.Message,
});
const createUiStreamTextMessage = (text, id) => ({
    id,
    isUser: false,
    text,
    type: UiMessageTypes.Message,
});
const createUiLoadingMessage = () => ({
    isUser: false,
    type: UiMessageTypes.Loading,
});
export const useStreamHandler = ({ conversationEndpointUrl, correlationId, initialState, customMessageHandlers, stateEqualityChecker = (prevState, nextState) => prevState === nextState, }) => {
    const logger = useClientLogger();
    // Compiler is not smart enough to infer the type of the initial state, so we need to cast it
    const [state, dispatch] = useReducer(streamReducer, {
        ...initialState,
        isStreaming: false,
    });
    // We need a local reference to the state to avoid stale & non-persistent state issues
    const latestState = useRef(state);
    useEffect(() => {
        latestState.current = state;
    }, [state]);
    const startConversation = (agentId, chatSource) => {
        const request = {
            type: PostMessageRequestTypes.StartConversation,
        };
        if (agentId) {
            request.agentId = agentId;
        }
        if (chatSource) {
            request.chatSource = chatSource;
        }
        handleMessageRequestWithoutLoader(request);
    };
    const addUserMessageToState = (message) => {
        dispatch({ type: 'ADD_MESSAGE', payload: createUserUiTextMessage(message) });
    };
    const sendUserMessage = (message, agentId, chatSource) => {
        addUserMessageToState(message);
        const request = {
            type: PostMessageRequestTypes.Message,
            payload: { content: message },
        };
        if (agentId) {
            request.agentId = agentId;
        }
        if (chatSource) {
            request.chatSource = chatSource;
        }
        handleMessageRequest(request);
    };
    const incomingMessageHandlers = useMemo(() => ({
        [PostMessageResponseTypes.Message]: (incomingMessage, currentState) => {
            if (incomingMessage.type !== PostMessageResponseTypes.Message) {
                return currentState;
            }
            const { uiMessages: existingUiMessages } = currentState;
            const { message, id } = incomingMessage.payload;
            if (existingUiMessages.length === 0) {
                return {
                    ...currentState,
                    uiMessages: [...existingUiMessages, createUiStreamTextMessage(message, undefined)],
                };
            }
            const previousExistingUiMessage = existingUiMessages[existingUiMessages.length - 1];
            // If the last message is from the user or has an ID, add a new message event as the last one is complete
            if (previousExistingUiMessage.type === UiMessageTypes.Message &&
                (previousExistingUiMessage.isUser || previousExistingUiMessage.id)) {
                return {
                    ...currentState,
                    uiMessages: [...existingUiMessages, createUiStreamTextMessage(message, undefined)],
                };
            }
            // ID message currently signifies the end of a stream, so add the id to the last UI message
            if (id) {
                const newUiMessages = existingUiMessages.map((existingUiMessage, index) => {
                    if (index === existingUiMessages.length - 1) {
                        return {
                            ...existingUiMessage,
                            id,
                        };
                    }
                    return existingUiMessage;
                });
                return {
                    ...currentState,
                    uiMessages: newUiMessages,
                };
            }
            // Append the message to the previous text message event
            if (previousExistingUiMessage.type === UiMessageTypes.Message) {
                const newUiMessages = existingUiMessages.map((existingUiMessage, index) => {
                    if (index === existingUiMessages.length - 1 &&
                        existingUiMessage.type === UiMessageTypes.Message) {
                        return {
                            ...existingUiMessage,
                            text: existingUiMessage.text + message,
                        };
                    }
                    return existingUiMessage;
                });
                return {
                    ...currentState,
                    uiMessages: newUiMessages,
                };
            }
            // The previous message event is not text and the stream hasn't ended, add a new message event
            return {
                ...currentState,
                uiMessages: [...existingUiMessages, createUiStreamTextMessage(message, undefined)],
            };
        },
        [PostMessageResponseTypes.Error]: (_, currentState) => {
            logger.warning('[VAP] Client - Received error message event type', {
                correlationId,
            });
            return {
                ...currentState,
                showError: true,
            };
        },
        [PostMessageResponseTypes.ResponseStarted]: (_, currentState) => ({ ...currentState }),
        ...customMessageHandlers,
    }), [correlationId, logger, customMessageHandlers]);
    // A separate handleMessageRequest is needed to display a message without the loader e.g. for the welcome message via startConversation
    // The default behaviour of displaying a loader before the message is exposed to consumers. We don't want to expose granular loader control until it's necessary
    const handleMessageRequestWithoutLoader = async (request) => {
        var _a;
        dispatch({ type: 'SET_INPUT_DISABLED' });
        // Flush batched state updates
        await Promise.resolve();
        let isLoading = true;
        let currentState = { ...latestState.current };
        try {
            const response = await fetch(conversationEndpointUrl, {
                method: 'POST',
                body: JSON.stringify(request),
                headers: {
                    'Coolblue-CorrelationId': correlationId,
                },
            });
            if (!response.ok || !response.body) {
                // Remove loader
                dispatch({ type: 'REMOVE_LAST_MESSAGE' });
                if (response.status >= 400 &&
                    response.status < 500 &&
                    ((_a = response.headers.get('Content-Type')) === null || _a === void 0 ? void 0 : _a.includes('application/json'))) {
                    const responseBody = (await response.json());
                    const error = new Error(`Status ${response.status}: ${response.statusText} - ${responseBody.message}`);
                    error.cause = responseBody.errors;
                    throw error;
                }
                throw new Error(`Status ${response.status}: ${response.statusText}${response.body ? '' : ' - No response body'}`);
            }
            const decoder = new TextDecoder();
            const reader = response.body.getReader();
            logger.info('[VAP] Client - New message event received', { correlationId });
            for (;;) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                const decodedValue = decoder.decode(value);
                const chunks = decodedValue.split('||');
                chunks.pop();
                chunks.every(chunk => {
                    const incomingMessage = JSON.parse(chunk);
                    const handler = incomingMessageHandlers[incomingMessage.type];
                    if (!handler) {
                        logger.warning(`[VAP] No handler found for message type: ${incomingMessage.type}`);
                        return true;
                    }
                    if (incomingMessage.type !== PostMessageResponseTypes.ResponseStarted && isLoading) {
                        currentState = {
                            ...currentState,
                            uiMessages: currentState.uiMessages.slice(0, -1),
                        };
                        isLoading = false;
                    }
                    const newState = handler(incomingMessage, currentState);
                    currentState = newState;
                    if (!stateEqualityChecker(state, newState)) {
                        dispatch({ type: 'SET_STATE', payload: newState });
                    }
                    return true;
                });
            }
        }
        catch (error) {
            logger.error('[VAP] Client send/receive message error', error, {
                correlationId: correlationId,
            });
            dispatch({ type: 'SET_SHOW_ERROR_TRUE' });
        }
        dispatch({ type: 'SET_INPUT_ENABLED' });
    };
    const handleMessageRequest = (request) => {
        dispatch({ type: 'ADD_MESSAGE', payload: createUiLoadingMessage() });
        handleMessageRequestWithoutLoader(request);
    };
    return { state, addUserMessageToState, sendUserMessage, startConversation, handleMessageRequest };
};
//# sourceMappingURL=useStreamHandler.js.map